/**
 * Ticket LM1: Bestätigung der Anforderung (doppelte Bestätigung).
 *
 * Bei Erfolg geht es direkt auf die Dankeseite des Freebies weiter. Ist der Link
 * abgelaufen, steht hier nicht nur ein Hinweis, sondern das Formular des
 * richtigen Freebies — niemand muss suchen.
 */

import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Layout } from "@/components/Layout";
import { LeadMagnetForm } from "@/components/lead-magnets/LeadMagnetForm";
import { confirmLeadMagnetSignup, getLeadMagnetPage } from "@/lib/lead-magnets.functions";
import type { PublicLeadMagnet } from "@/lib/lead-magnets";
import { pushDataLayerEvent } from "@/lib/datalayer";

type Search = { token?: string };

const TITLE = "Anforderung bestätigen | creategy";

export const Route = createFileRoute("/kostenlos/bestaetigen")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content: "Bestätigung Ihrer Anforderung einer kostenlosen Unterlage von creategy.",
      },
      { property: "og:title", content: TITLE },
      {
        property: "og:description",
        content: "Bestätigung Ihrer Anforderung einer kostenlosen Unterlage von creategy.",
      },
      { property: "og:type", content: "website" },
    ],
  }),
  component: LeadMagnetConfirmPage,
});

type State =
  | { kind: "loading" }
  | { kind: "unknown" }
  | { kind: "expired"; magnet: PublicLeadMagnet | null };

function LeadMagnetConfirmPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const confirm = useServerFn(confirmLeadMagnetSignup);
  const loadMagnet = useServerFn(getLeadMagnetPage);
  const [state, setState] = useState<State>(token ? { kind: "loading" } : { kind: "unknown" });
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    let active = true;

    (async () => {
      try {
        const result = await confirm({ data: { token } });
        if (!active) return;
        if (result.status === "ok") {
          // Freebie-Abschluss: nur bei der ersten Bestätigung, das entscheidet der Server.
          if (result.firstCompletion) {
            pushDataLayerEvent({ event: "leadmagnet_download", leadmagnet: result.slug });
          }
          navigate({
            to: "/kostenlos/$slug/$step",
            params: { slug: result.slug, step: result.thanksSlug },
            search: {
              zugang: result.deliveryToken ?? undefined,
              zugriff: result.toolOutcome,
            },
            replace: true,
          });
          return;
        }
        if (result.status === "expired") {
          const magnet = await loadMagnet({ data: { slug: result.slug } });
          if (active) setState({ kind: "expired", magnet: magnet ?? null });
          return;
        }
        setState({ kind: "unknown" });
      } catch {
        if (active) setState({ kind: "unknown" });
      }
    })();

    return () => {
      active = false;
    };
  }, [token, confirm, loadMagnet, navigate]);

  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[900px] px-6 md:px-12">
          {state.kind === "loading" && (
            <>
              <h1 className="heading-h1 text-primary">Einen Moment bitte.</h1>
              <p className="mt-8 font-body text-base text-muted-foreground">
                Ihre Bestätigung wird geprüft.
              </p>
            </>
          )}

          {state.kind === "expired" && (
            <>
              <h1 className="heading-h1 text-primary">Der Bestätigungslink ist abgelaufen.</h1>
              <p className="mt-8 max-w-[68ch] font-body text-base leading-relaxed text-foreground md:text-lg">
                Bestätigungslinks gelten 72 Stunden. Fordern Sie
                {state.magnet ? ` „${state.magnet.title}“ ` : " die Unterlage "}
                einfach direkt hier erneut an — Sie erhalten sofort eine neue E-Mail.
              </p>
              {state.magnet && (
                <div className="mt-10 rounded-sm border border-foreground/15 p-6 md:p-8">
                  <LeadMagnetForm
                    magnet={state.magnet}
                    notice="Ihr Bestätigungslink war zu alt. Bitte senden Sie das Formular noch einmal ab."
                  />
                </div>
              )}
            </>
          )}

          {state.kind === "unknown" && (
            <>
              <h1 className="heading-h1 text-primary">Dieser Link ist nicht gültig.</h1>
              <p className="mt-8 max-w-[68ch] font-body text-base leading-relaxed text-foreground md:text-lg">
                Bitte öffnen Sie den Link aus der Bestätigungsmail erneut. Hilft das nicht,
                schreiben Sie uns an{" "}
                <a className="underline" href="mailto:hello@creategy.de">
                  hello@creategy.de
                </a>
                .
              </p>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
